import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AllAccessLevelOptions, DefaultAccessLevelOptions } from '@/utils/accessUtils';

import type { Category } from '../../models/Category';
import type { OU } from '../../models/Ou';
import type { Schema } from '../../models/Schema';
import type { ToastMessage } from '../../models/toastMessage';
import { FuelPortalService } from '../../services/fuel-portal/fuel-portal-service';
import { LocalStorageService } from '../../services/local-storage';
import { dateUtils } from '../../utils/dateUtils';
import type { RootState } from '.';

export type ApplicationDataState = {
  ouData: OU | null | undefined;
  schemas: Schema[];
  categories: Category[];
  defaultVariantCodes: string[];
  ouDataLoading: boolean;
  toastMessage: ToastMessage | any;
  confirmMessage?: string;
  promptMessage?: string;
  switchOuData?: OU | any;
};

export const initialState: ApplicationDataState = {
  ouData: null,
  schemas: [],
  categories: [],
  defaultVariantCodes: [],
  ouDataLoading: false,
  toastMessage: null,
  confirmMessage: null,
  switchOuData: null,
  uiFlags: null,
};

const enhanceOUData = (ouData: OU) => {
  if (ouData) {
    ouData.timezoneAbbreviation = dateUtils.getTimezoneAbbreviation(ouData.timezone);
  }
  return ouData;
};

const applicationDataSlice = createSlice({
  name: 'applicationData',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.ouDataLoading = true;
    },
    SetConfirmMessage: (state, action: any) => {
      state.confirmMessage = action.payload;
    },
    SetSnackbarMessage: (state, action: any) => {
      // console.log('state--SetSnackbarMessage', action)
      state.toastMessage = action.payload;
    },
    SetPromptMessage: (state, action: any) => {
      state.promptMessage = action.payload;
    },
    SetOuData: (state, action: any) => {
      // console.log('SetOuData', { state, action });
      const ouData: OU = enhanceOUData(action.payload);
      state.ouData = ouData;
      state.ouDataLoading = false;
    },
    SetSwitchOuData: (state, action: any) => {
      state.switchOuData = action.payload;
      state.ouDataLoading = false;
    },
    SetCategories: (state, action: any) => {
      state.categories = action.payload;
      state.ouDataLoading = false;
    },
    SetSchemas: (state, action: any) => {
      state.schemas = action.payload;
      state.ouDataLoading = false;
    },
    SetDefaultVariantCodes: (state, action: any) => {
      state.defaultVariantCodes = action.payload;
      state.ouDataLoading = false;
    },
  },
});

export const {
  startLoading,
  SetOuData,
  SetCategories,
  SetSchemas,
  SetDefaultVariantCodes,
  SetSnackbarMessage,
  SetConfirmMessage,
  SetPromptMessage,
  SetSwitchOuData,
} = applicationDataSlice.actions;

// export permission selector to get the slice in any component
export const applicationDataSelector = (state: RootState) => state.applicationData;
// export The reducer
const applicationDataReducer = applicationDataSlice.reducer;
export default applicationDataReducer;

export const processOuData = (ouData: OU, state: ApplicationDataState, dispatch) => {
  const { schemas, categories, metaData } = ouData;

  ouData.timezoneAbbreviation = dateUtils.getTimezoneAbbreviation(ouData.timezone);
  if (ouData.metaData == null) {
    ouData.metaData = {};
  }
  ouData.metaData.defaultAccessLevelOptions =
    ouData.metaData.enablePaywall === true ? AllAccessLevelOptions : DefaultAccessLevelOptions;

  // console.log('processOuData', { ouData });

  dispatch(SetOuData(ouData));
  dispatch(SetCategories(categories ? categories.filter((cat) => !cat.isDeleted) : []));
  dispatch(SetSchemas(ouData.schemas));

  // If OU update to  multivariants so we have to update every time.
  dispatch(SetDefaultVariantCodes(metaData.defaultVariants ? metaData.defaultVariants : ['600K360AWS', '3500K720AWS']));
};

export const getCurrentOu = createAsyncThunk(
  // action type string
  'applicationData/getOu',
  async (fullInfo: boolean, { getState, dispatch }) => {
    // console.log('get ou full info');
    const state = getState() as RootState;
    const { fuelUserAccess } = state;
    let defaultLoginOu = LocalStorageService.getItem('defaultLoginOu');
    if (!defaultLoginOu) defaultLoginOu = fuelUserAccess.fuelUserAccessData.defaultLoginOu;
    let ou = state.applicationData.ouData;
    if (ou == null && defaultLoginOu) {
      ou = await FuelPortalService.getOuData(defaultLoginOu, fullInfo);
      processOuData(ou, state.applicationData, dispatch);
    }
    return ou;
  }
);

export const updateOu = createAsyncThunk(
  // action type string
  'applicationData/updateOu',
  async ({ ouId, forceUpdate }: { ouId: string; forceUpdate: boolean }, { getState, dispatch }) => {
    const state = getState() as RootState;
    let ou = state.applicationData.ouData;
    if (ou == null || ou.ouId !== ouId || forceUpdate) {
      ou = await FuelPortalService.getOuData(ouId, true);
      processOuData(ou, state.applicationData, dispatch);
      dispatch(SetSwitchOuData(ou));
    }
    return ou;
  }
);

export const setApplicationMessage = createAsyncThunk(
  'applicationData/setApplicationMessage',
  async (value: ToastMessage, { dispatch }) => {
    if (!value.severity) {
      value.severity = 'success';
    }
    dispatch(SetSnackbarMessage(value));
  }
);

export const setConfirmationMessage = createAsyncThunk(
  'applicationData/setConfirmationMessage',
  async (value: string | null, { dispatch }) => {
    dispatch(SetConfirmMessage(value));
  }
);

export const setUserPromptMessage = createAsyncThunk(
  'applicationData/setUserPromptMessage',
  async (value: string | null, { dispatch }) => {
    dispatch(SetPromptMessage(value));
  }
);
